import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';

import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';

export interface UseCommentReplyParams {
	commentId: string;
	parentCommentId?: string | null;
	isFooterComment: boolean;
	replyToComment?: (replyToCommentId: string) => void;
}

export interface UseCommentReplyReturnType {
	handleReply: () => void;
	handleReplyWithoutMention: () => void;
}

export const useCommentReply = ({
	commentId,
	parentCommentId,
	isFooterComment,
	replyToComment,
}: UseCommentReplyParams): UseCommentReplyReturnType => {
	const replyPressTracing = usePressTracing('start-reply-comment-action');

	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();
	const { setReplyToCommentId } = useCommentsPanelActions();

	/**
	 * Generic handler that checks for content changes and shows warning dialog if needed
	 */
	const handleReplyAction = (replyAction: () => void) => {
		if (hasContentChanged) {
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					replyAction();
				},
			});
		} else {
			replyAction();
		}
	};

	const handleReply = () => {
		const replyToCommentId = isFooterComment ? commentId : parentCommentId || commentId;
		handleReplyAction(() => replyToCommentWithPressTracing(replyToCommentId));
	};

	const handleReplyWithoutMention = () => {
		handleReplyAction(() => replyWithPressTracing());
	};

	const replyWithPressTracing = () => {
		replyPressTracing();
		setReplyToCommentId({
			parentCommentId: parentCommentId || commentId,
			location: isFooterComment ? LocationValue.FOOTER : LocationValue.PANEL,
		});
	};

	const replyToCommentWithPressTracing = (replyToCommentId: string) => {
		if (replyToComment) {
			replyPressTracing();
			replyToComment(replyToCommentId);
		}
	};

	return {
		handleReply,
		handleReplyWithoutMention,
	};
};
